import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Dropdown, Alert, Spinner, Row, Col, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import TicketService from '../../../services/TicketService';

const AdminTicketQueue = () => {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    category: ''
  });
  const [metadata, setMetadata] = useState(null);

  useEffect(() => {
    fetchMetadata();
    fetchTickets();
  }, [filters]);

  const fetchMetadata = async () => {
    try {
      const response = await TicketService.getMetadata();
      setMetadata(response.data);
    } catch (err) {
      console.error('Error fetching metadata:', err);
    }
  };

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const activeFilters = {};
      if (filters.status) activeFilters.status = filters.status;
      if (filters.priority) activeFilters.priority = filters.priority;
      if (filters.category) activeFilters.category = filters.category;
      
      const response = await TicketService.getAllTickets(activeFilters);
      setTickets(response.data || []);
      setStats(response.stats || null);
      setError(null);
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError('Failed to load tickets. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      priority: '',
      category: ''
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      open: 'danger',
      in_progress: 'warning',
      resolved: 'success',
      closed: 'secondary'
    };
    const labels = {
      open: 'Open',
      in_progress: 'In Progress',
      resolved: 'Resolved',
      closed: 'Closed'
    };
    return <Badge bg={variants[status] || 'secondary'}>{labels[status] || status}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      low: 'success',
      medium: 'info',
      high: 'warning',
      urgent: 'danger'
    };
    return <Badge bg={variants[priority] || 'secondary'} className="text-uppercase">{priority}</Badge>;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      technical: 'Technical',
      billing: 'Billing',
      feature_request: 'Feature',
      general: 'General',
      account: 'Account'
    };
    return labels[category] || category;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="admin-ticket-queue">
      <Card>
        <Card.Header>
          <h4 className="mb-0">Support Ticket Queue</h4>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          {/* Stats */}
          {stats && (
            <Row className="mb-4">
              <Col md={2} xs={6} className="mb-2">
                <Card className="text-center bg-danger text-white">
                  <Card.Body className="py-3">
                    <h3 className="mb-1">{stats.open || 0}</h3>
                    <small>Open</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2} xs={6} className="mb-2">
                <Card className="text-center bg-warning">
                  <Card.Body className="py-3">
                    <h3 className="mb-1">{stats.in_progress || 0}</h3>
                    <small>In Progress</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2} xs={6} className="mb-2">
                <Card className="text-center bg-success text-white">
                  <Card.Body className="py-3">
                    <h3 className="mb-1">{stats.resolved || 0}</h3>
                    <small>Resolved</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2} xs={6} className="mb-2">
                <Card className="text-center bg-secondary text-white">
                  <Card.Body className="py-3">
                    <h3 className="mb-1">{stats.closed || 0}</h3>
                    <small>Closed</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4} xs={12} className="mb-2">
                <Card className="text-center bg-primary text-white">
                  <Card.Body className="py-3">
                    <h3 className="mb-1">{stats.total || 0}</h3>
                    <small>Total Tickets</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Filters */}
          <Card className="mb-3 bg-light">
            <Card.Body>
              <Row>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <option value="">All Statuses</option>
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Priority</Form.Label>
                    <Form.Select
                      value={filters.priority}
                      onChange={(e) => handleFilterChange('priority', e.target.value)}
                    >
                      <option value="">All Priorities</option>
                      <option value="urgent">Urgent</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Category</Form.Label>
                    <Form.Select
                      value={filters.category}
                      onChange={(e) => handleFilterChange('category', e.target.value)}
                    >
                      <option value="">All Categories</option>
                      {metadata?.categories?.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3} className="d-flex align-items-end">
                  <Button variant="outline-secondary" onClick={clearFilters} className="w-100">
                    <i className="flaticon-refresh mr-2"></i>
                    Clear Filters
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Tickets Table */}
          {tickets.length === 0 ? (
            <div className="text-center py-5">
              <i className="flaticon-support" style={{ fontSize: '48px', color: '#ccc' }}></i>
              <p className="mt-3 text-muted">No tickets found matching your filters.</p>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>User</th>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Replies</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map(ticket => (
                  <tr key={ticket.id} className={ticket.priority === 'urgent' ? 'table-danger' : ''}>
                    <td>#{ticket.id}</td>
                    <td>
                      <div>{ticket.user_name || 'Unknown'}</div>
                      <small className="text-muted">{ticket.user_email}</small>
                    </td>
                    <td>
                      <Link to={`/tickets/${ticket.id}`} className="text-decoration-none">
                        {ticket.title}
                      </Link>
                    </td>
                    <td>{getCategoryLabel(ticket.category)}</td>
                    <td>{getPriorityBadge(ticket.priority)}</td>
                    <td>{getStatusBadge(ticket.status)}</td>
                    <td>
                      <Badge bg="light" text="dark">
                        {ticket.reply_count || 0}
                      </Badge>
                    </td>
                    <td>{formatDate(ticket.created_at)}</td>
                    <td>
                      <Link to={`/tickets/${ticket.id}`} className="btn btn-sm btn-outline-primary">
                        <i className="flaticon-view mr-1"></i>
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default AdminTicketQueue;
